import { useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, Check, Monitor, Moon, Sun, User } from 'lucide-react';
import { useTheme } from 'next-themes';

import { MAIN_TABS } from '../memberNavigation';

import { useMemberPreferences } from './MemberPreferencesContext';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  applyMemberDisplaySize,
  MEMBER_DISPLAY_SIZE_OPTIONS,
  normalizeMemberPreferences,
} from '@/services/memberPreferences';

const APPEARANCE_OPTIONS = [
  { value: 'system', label: 'System', Icon: Monitor },
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'dark', label: 'Dark', Icon: Moon },
];

const samePreferences = (left, right) => JSON.stringify(left) === JSON.stringify(right);

const MemberPreferencesDialog = ({ open, onOpenChange, returnFocusTarget }) => {
  const { setTheme } = useTheme();
  const {
    account,
    preferences: savedPreferences,
    loading,
    ready,
    loadError,
    reload,
    save,
  } = useMemberPreferences();
  const [preferences, setPreferences] = useState(savedPreferences);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setPreferences(savedPreferences);
    }
  }, [open, savedPreferences]);

  useEffect(() => {
    if (!open) return;
    setMessage('');
    setError('');
  }, [open]);

  const update = (key, value) => {
    setPreferences((current) => normalizeMemberPreferences({ ...current, [key]: value }));
    setMessage('');
    if (key === 'appearance') setTheme(value);
    if (key === 'display_size') applyMemberDisplaySize(value);
  };

  const move = (tabId, delta) => {
    const order = [...preferences.navigation_order];
    const index = order.indexOf(tabId);
    const nextIndex = index + delta;
    if (index < 0 || nextIndex < 0 || nextIndex >= order.length) return;
    [order[index], order[nextIndex]] = [order[nextIndex], order[index]];
    update('navigation_order', order);
  };

  const close = (nextOpen) => {
    if (!nextOpen && !saving && !samePreferences(preferences, savedPreferences)) {
      setTheme(savedPreferences.appearance);
      applyMemberDisplaySize(savedPreferences.display_size);
    }
    onOpenChange(nextOpen);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const normalized = await save(preferences);
      setPreferences(normalized);
      setMessage('Preferences saved.');
    } catch (requestError) {
      setError(requestError.message || 'Unable to save your preferences.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className="max-h-[calc(100dvh-1rem)] w-[calc(100vw-1rem)] max-w-2xl overflow-y-auto p-4 sm:p-6"
        onCloseAutoFocus={(event) => {
          if (!returnFocusTarget) return;
          event.preventDefault();
          returnFocusTarget.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>Personal preferences</DialogTitle>
          <DialogDescription>Choose how your member portal looks and which destinations appear.</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="account">
          <TabsList className="grid h-auto w-full grid-cols-3">
            <TabsTrigger value="account">Account</TabsTrigger>
            <TabsTrigger value="display">Display</TabsTrigger>
            <TabsTrigger value="navigation">Navigation</TabsTrigger>
          </TabsList>
          <TabsContent value="account" className="pt-3">
            <div className="flex min-w-0 items-start gap-3 rounded-lg border p-4">
              <User className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
              <div className="min-w-0">
                <p className="font-medium break-words">{account.name || 'Member'}</p>
                <p className="break-all text-sm text-muted-foreground">{account.email}</p>
                <p className="mt-2 text-xs capitalize text-muted-foreground">{account.auth_type || 'member'} account</p>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="display" className="space-y-5 pt-3">
            <fieldset>
              <legend className="mb-2 text-sm font-medium">Appearance</legend>
              <div className="grid gap-2 sm:grid-cols-3">
                {APPEARANCE_OPTIONS.map(({ value, label, Icon }) => (
                  <label key={value} className={`cursor-pointer rounded-lg border p-3 ${preferences.appearance === value ? 'border-amber-600 bg-amber-50 dark:bg-amber-950/30' : ''}`}>
                    <input className="sr-only" type="radio" name="member-appearance" checked={preferences.appearance === value} onChange={() => update('appearance', value)} />
                    <span className="flex items-center gap-2 font-medium"><Icon className="h-4 w-4" />{label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <fieldset>
              <legend className="mb-2 text-sm font-medium">Display size</legend>
              <div className="grid gap-2 sm:grid-cols-2">
                {MEMBER_DISPLAY_SIZE_OPTIONS.map(({ value, label, scale }) => (
                  <label key={value} className={`flex cursor-pointer justify-between rounded-lg border p-3 ${preferences.display_size === value ? 'border-amber-600 bg-amber-50 dark:bg-amber-950/30' : ''}`}>
                    <span>{label} <span className="text-xs text-muted-foreground">{scale}</span></span>
                    <input type="radio" name="member-display-size" checked={preferences.display_size === value} onChange={() => update('display_size', value)} />
                  </label>
                ))}
              </div>
            </fieldset>
          </TabsContent>
          <TabsContent value="navigation" className="space-y-3 pt-3">
            <p className="text-sm text-muted-foreground">Reorder destinations or hide ones you do not use. Progress remains your home.</p>
            <ul className="space-y-2" aria-label="Member navigation destination order">
              {preferences.navigation_order.map((tabId, index) => {
                const tab = MAIN_TABS.find(({ id }) => id === tabId);
                if (!tab) return null;
                const Icon = tab.icon;
                const hidden = preferences.navigation_hidden.includes(tabId);
                return (
                  <li key={tabId} className="flex items-center gap-2 rounded-lg border p-2">
                    <Icon className="h-4 w-4 shrink-0 text-amber-700" />
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">{tab.label}</span>
                    <Button type="button" size="icon" variant="ghost" className="h-8 w-8" disabled={index === 0} onClick={() => move(tabId, -1)} aria-label={`Move ${tab.label} up`}><ArrowUp className="h-4 w-4" /></Button>
                    <Button type="button" size="icon" variant="ghost" className="h-8 w-8" disabled={index === preferences.navigation_order.length - 1} onClick={() => move(tabId, 1)} aria-label={`Move ${tab.label} down`}><ArrowDown className="h-4 w-4" /></Button>
                    <Switch
                      checked={!hidden}
                      disabled={tabId === 'progress'}
                      onCheckedChange={(checked) => update('navigation_hidden', checked ? preferences.navigation_hidden.filter((id) => id !== tabId) : [...preferences.navigation_hidden, tabId])}
                      aria-label={`Show ${tab.label}`}
                    />
                  </li>
                );
              })}
            </ul>
          </TabsContent>
        </Tabs>

        <div aria-live="polite" className="min-h-5 text-sm">
          {loading && <p>Loading preferences…</p>}
          {!loading && loadError && (
            <div className="flex flex-wrap items-center gap-2 text-red-600" role="alert">
              <span>{loadError} Your cached choices remain active, but saving is disabled.</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => reload().catch(() => {})}
              >
                Retry
              </Button>
            </div>
          )}
          {error && <p className="text-red-600" role="alert">{error}</p>}
          {message && <p className="flex items-center gap-1 text-green-700"><Check className="h-4 w-4" />{message}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)} disabled={saving}>Close</Button>
          <Button onClick={handleSave} disabled={loading || !ready || saving || samePreferences(preferences, savedPreferences)}>{saving ? 'Saving…' : 'Save preferences'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MemberPreferencesDialog;
